import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import {
  Calendar,
  Clock,
  Send,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  MessageSquare,
  Users,
  IndianRupee,
  Shield,
  History,
  Download,
  RefreshCw,
  Zap,
  Timer,
  Target,
  BarChart3,
  FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Schedule Type Selector
const ScheduleTypeCard = ({ type, selected, onSelect, title, description, icon: Icon }) => (
  <Card 
    className={`cursor-pointer transition-all ${
      selected ? 'border-violet-500 bg-violet-500/5 shadow-lg' : 'hover:border-violet-500/50'
    }`}
    onClick={() => onSelect(type)}
  >
    <CardContent className="p-4 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${selected ? 'bg-violet-500 text-white' : 'bg-muted'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {selected && <CheckCircle2 className="w-5 h-5 text-violet-500" />}
    </CardContent>
  </Card>
);

// Campaign Scheduling Modal
export function CampaignScheduleModal({ campaign, onClose, onScheduled }) {
  const [scheduleType, setScheduleType] = useState('immediate');
  const [scheduledTime, setScheduledTime] = useState('');
  const [enableAbTest, setEnableAbTest] = useState(false);
  const [abTestConfig, setAbTestConfig] = useState({
    split_percentage: 50,
    test_duration_hours: 4,
    winning_metric: 'response_rate',
    auto_send_winner: true
  });
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [fallbackChannel, setFallbackChannel] = useState('sms');
  const [sendRateLimit, setSendRateLimit] = useState('standard');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [loading, setLoading] = useState(false);

  // Get message variants from campaign
  const messageVariants = campaign?.message_variants || campaign?.ai_generated_messages || [];

  const handleVariantToggle = (index) => {
    setSelectedVariants(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else if (prev.length < 2) {
        return [...prev, index];
      }
      return prev;
    });
  };

  const handleSchedule = async () => {
    setLoading(true);
    try {
      const payload = {
        schedule_type: scheduleType,
        scheduled_time: scheduledTime || undefined,
        timezone: timezone,
        enable_ab_test: enableAbTest,
        ab_test_config: enableAbTest ? {
          ...abTestConfig,
          variant_indices: selectedVariants.length === 2 ? selectedVariants : [0, 1]
        } : undefined,
        fallback_channel: fallbackChannel,
        send_rate: sendRateLimit
      };
      
      const response = await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/schedule`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Campaign scheduled successfully!');
        onScheduled?.(response.data);
      }
    } catch (err) {
      console.error('Schedule error:', err);
      toast.error(err.response?.data?.detail || 'Failed to schedule campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="campaign-schedule-modal">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Schedule Campaign</h2>
          <p className="text-sm text-muted-foreground">{campaign.name}</p>
        </div>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>

      {/* Schedule Type Selection */}
      <div className="space-y-3">
        <h3 className="font-semibold">When to send?</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <ScheduleTypeCard
            type="immediate"
            selected={scheduleType === 'immediate'}
            onSelect={setScheduleType}
            title="Send Now"
            description="Start sending immediately"
            icon={Zap}
          />
          <ScheduleTypeCard
            type="scheduled"
            selected={scheduleType === 'scheduled'}
            onSelect={setScheduleType}
            title="Schedule"
            description="Pick a specific date and time"
            icon={Calendar}
          />
          <ScheduleTypeCard
            type="optimal"
            selected={scheduleType === 'optimal'}
            onSelect={setScheduleType}
            title="Optimal Time"
            description="AI picks best time (10 AM weekday)"
            icon={Target}
          />
        </div>
      </div>

      {/* Scheduled Time Input */}
      {scheduleType === 'scheduled' && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Date & Time (IST)</label>
          <Input
            type="datetime-local"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className="max-w-xs"
          />
        </div>
      )}

      {/* Advanced Options */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="font-semibold">Advanced Options</h3>
        
        {/* A/B Testing Section */}
        <Card className={`transition-all ${enableAbTest ? 'border-violet-500 bg-violet-500/5' : ''}`}>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="ab-test"
                  checked={enableAbTest}
                  onCheckedChange={setEnableAbTest}
                />
                <label htmlFor="ab-test" className="text-sm font-medium cursor-pointer">
                  Enable A/B Testing
                </label>
              </div>
              {enableAbTest && (
                <Badge className="bg-violet-500">2 Variants</Badge>
              )}
            </div>
            
            {enableAbTest && (
              <div className="space-y-4 pt-3 border-t">
                {/* Variant Selection */}
                {messageVariants.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Select Test Variants (pick 2)</label>
                    <div className="grid gap-2">
                      {messageVariants.slice(0, 4).map((variant, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedVariants.includes(idx) 
                              ? 'border-violet-500 bg-violet-500/10' 
                              : 'hover:border-muted-foreground/50'
                          }`}
                          onClick={() => handleVariantToggle(idx)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                selectedVariants.includes(idx) ? 'bg-violet-500 text-white' : 'bg-muted'
                              }`}>
                                {String.fromCharCode(65 + idx)}
                              </div>
                              <span className="text-sm">{variant.label || `Variant ${String.fromCharCode(65 + idx)}`}</span>
                            </div>
                            {selectedVariants.includes(idx) && (
                              <CheckCircle2 className="w-4 h-4 text-violet-500" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {variant.message?.substring(0, 100) || variant.content?.substring(0, 100) || 'Message preview'}...
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Split Configuration */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Test Split (%)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="10"
                        max="50"
                        step="10"
                        value={abTestConfig.split_percentage}
                        onChange={(e) => setAbTestConfig(prev => ({ ...prev, split_percentage: parseInt(e.target.value) }))}
                        className="flex-1"
                      />
                      <span className="text-sm font-mono w-12">{abTestConfig.split_percentage}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {abTestConfig.split_percentage}% of audience split equally between variants
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Test Duration</label>
                    <select 
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      value={abTestConfig.test_duration_hours}
                      onChange={(e) => setAbTestConfig(prev => ({ ...prev, test_duration_hours: parseInt(e.target.value) }))}
                    >
                      <option value={2}>2 hours</option>
                      <option value={4}>4 hours</option>
                      <option value={8}>8 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours</option>
                    </select>
                  </div>
                </div>
                
                {/* Winning Metric */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Winning Metric</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'response_rate', label: 'Response Rate', icon: Target },
                      { id: 'delivery_rate', label: 'Delivery Rate', icon: CheckCircle2 },
                      { id: 'revenue', label: 'Revenue', icon: Zap }
                    ].map(metric => (
                      <Button
                        key={metric.id}
                        variant={abTestConfig.winning_metric === metric.id ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 gap-1"
                        onClick={() => setAbTestConfig(prev => ({ ...prev, winning_metric: metric.id }))}
                      >
                        <metric.icon className="w-3 h-3" />
                        {metric.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Auto-send winner */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="auto-winner"
                    checked={abTestConfig.auto_send_winner}
                    onCheckedChange={(checked) => setAbTestConfig(prev => ({ ...prev, auto_send_winner: checked }))}
                  />
                  <label htmlFor="auto-winner" className="text-sm">
                    Automatically send winning variant to remaining {100 - abTestConfig.split_percentage}% audience
                  </label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Rate */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Send Rate</label>
          <div className="flex gap-2">
            {[
              { id: 'standard', label: 'Standard', desc: '100 msg/sec' },
              { id: 'throttled', label: 'Throttled', desc: '50 msg/sec' },
              { id: 'burst', label: 'Burst', desc: '200 msg/sec' }
            ].map(rate => (
              <Button
                key={rate.id}
                variant={sendRateLimit === rate.id ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() => setSendRateLimit(rate.id)}
              >
                <div className="text-center">
                  <p>{rate.label}</p>
                  <p className="text-xs opacity-70">{rate.desc}</p>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Fallback Channel */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Fallback Channel</label>
          <div className="flex gap-2">
            {['sms', 'whatsapp', 'none'].map(channel => (
              <Button
                key={channel}
                variant={fallbackChannel === channel ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFallbackChannel(channel)}
              >
                {channel === 'none' ? 'No Fallback' : channel.toUpperCase()}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            If primary channel fails, retry with fallback
          </p>
        </div>
      </div>

      {/* Summary */}
      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/5">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Audience</p>
              <p className="text-lg font-bold">
                {(campaign.confirmed_audience_size || campaign.estimated_audience_size || 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Channel</p>
              <p className="text-lg font-bold">
                {(campaign.channels?.[0] || 'SMS').toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Schedule</p>
              <p className="text-lg font-bold capitalize">{scheduleType}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">A/B Test</p>
              <p className="text-lg font-bold">{enableAbTest ? 'Yes' : 'No'}</p>
            </div>
          </div>
          {enableAbTest && (
            <div className="mt-3 pt-3 border-t text-center">
              <p className="text-xs text-muted-foreground">
                {abTestConfig.split_percentage}% test audience • {abTestConfig.test_duration_hours}h test • {abTestConfig.auto_send_winner ? 'Auto-send winner' : 'Manual selection'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSchedule}
          disabled={loading || (scheduleType === 'scheduled' && !scheduledTime) || (enableAbTest && selectedVariants.length !== 2 && messageVariants.length > 1)}
          className="gap-2"
          data-testid="schedule-campaign-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
          Schedule Campaign
        </Button>
      </div>
    </div>
  );
}

// Execution Confirmation Modal
export function ExecutionConfirmationModal({ campaign, onClose, onExecuted }) {
  const [confirmationSummary, setConfirmationSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [checklist, setChecklist] = useState({
    message_reviewed: false,
    audience_confirmed: false,
    cost_approved: false,
    understand_irreversible: false
  });
  const [executorNotes, setExecutorNotes] = useState('');

  useEffect(() => {
    fetchConfirmationSummary();
  }, [campaign.campaign_id]);

  const fetchConfirmationSummary = async () => {
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaign.campaign_id}/confirmation-summary`,
        { withCredentials: true }
      );
      setConfirmationSummary(response.data.summary);
    } catch (err) {
      console.error('Fetch summary error:', err);
      toast.error('Failed to load confirmation summary');
    } finally {
      setLoading(false);
    }
  };

  const allChecked = Object.values(checklist).every(v => v);

  const handleExecute = async () => {
    if (!allChecked) {
      toast.error('Please confirm all items in the checklist');
      return;
    }

    setExecuting(true);
    try {
      const response = await axios.post(
        `${API}/api/campaigns/${campaign.campaign_id}/execute`,
        {
          confirmed: true,
          confirmation_checklist: checklist,
          executor_notes: executorNotes
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Campaign execution started!', { duration: 5000 });
        onExecuted?.(response.data);
      }
    } catch (err) {
      console.error('Execute error:', err);
      toast.error(err.response?.data?.detail || 'Failed to execute campaign');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const summary = confirmationSummary;

  return (
    <div className="space-y-6" data-testid="execution-confirmation-modal">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Final Confirmation</h2>
          <p className="text-sm text-muted-foreground">Review all details before executing</p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <div>
            <p className="font-semibold text-amber-700">This action cannot be undone</p>
            <p className="text-sm text-muted-foreground">
              Once you execute, messages will be sent to all recipients
            </p>
          </div>
        </div>
      </div>

      {/* Campaign Summary */}
      {summary && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Audience */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-5 h-5 text-violet-500" />
                <h4 className="font-semibold">Target Audience</h4>
              </div>
              <p className="text-2xl font-bold">{summary.audience?.formatted_size}</p>
              <p className="text-sm text-muted-foreground">{summary.audience?.segment_name}</p>
            </CardContent>
          </Card>

          {/* Message */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare className="w-5 h-5 text-violet-500" />
                <h4 className="font-semibold">Message</h4>
              </div>
              <Badge>{summary.message?.channel}</Badge>
              <p className="text-sm mt-2">{summary.message?.character_count} characters</p>
              <p className="text-xs text-muted-foreground">
                {summary.message?.sms_count} SMS per recipient
              </p>
            </CardContent>
          </Card>

          {/* Cost */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <IndianRupee className="w-5 h-5 text-emerald-500" />
                <h4 className="font-semibold">Estimated Cost</h4>
              </div>
              <p className="text-2xl font-bold text-emerald-500">
                {summary.cost_estimate?.formatted}
              </p>
              <p className="text-sm text-muted-foreground">
                ₹{summary.cost_estimate?.cost_per_message} per message
              </p>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-5 h-5 text-blue-500" />
                <h4 className="font-semibold">Schedule</h4>
              </div>
              <Badge className={summary.schedule?.type === 'immediate' ? 'bg-red-500' : 'bg-blue-500'}>
                {summary.schedule?.type?.toUpperCase()}
              </Badge>
              {summary.schedule?.scheduled_at && (
                <p className="text-sm mt-2">
                  {new Date(summary.schedule.scheduled_at).toLocaleString('en-IN')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Message Preview */}
      {summary?.message?.content && (
        <Card className="border-violet-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Message Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 rounded-lg bg-muted/50 whitespace-pre-wrap text-sm">
              {summary.message.content}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Trail */}
      {summary?.approval_trail?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" />
              Approval Trail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.approval_trail.map((approval, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-muted/30">
                  <span className="font-medium">{approval.step?.replace(/_/g, ' ')}</span>
                  <span className="text-muted-foreground">{approval.approved_by}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Checklist */}
      <Card className="border-red-500/30 bg-red-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-red-700">Confirmation Checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'message_reviewed', label: 'I have reviewed the message content and confirm it is accurate' },
            { key: 'audience_confirmed', label: `I confirm the target audience of ${summary?.audience?.formatted_size || '0'} customers` },
            { key: 'cost_approved', label: `I approve the estimated cost of ${summary?.cost_estimate?.formatted || '₹0'}` },
            { key: 'understand_irreversible', label: 'I understand this action cannot be undone once messages are sent' }
          ].map(item => (
            <div key={item.key} className="flex items-start gap-3">
              <Checkbox
                id={item.key}
                checked={checklist[item.key]}
                onCheckedChange={(checked) => setChecklist(prev => ({ ...prev, [item.key]: checked }))}
              />
              <label htmlFor={item.key} className="text-sm cursor-pointer">{item.label}</label>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Executor Notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Notes (Optional)</label>
        <Textarea
          placeholder="Add any notes about this execution..."
          value={executorNotes}
          onChange={(e) => setExecutorNotes(e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleExecute}
          disabled={!allChecked || executing}
          className="gap-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
        >
          {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Execute Campaign
        </Button>
      </div>
    </div>
  );
}

// Campaign Progress Tracker
export function CampaignProgressTracker({ campaignId, onClose }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchProgress();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchProgress, 5000); // Refresh every 5 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [campaignId, autoRefresh]);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaignId}/progress`,
        { withCredentials: true }
      );
      setProgress(response.data);
      
      // Stop auto-refresh if completed
      if (response.data.progress?.is_complete) {
        setAutoRefresh(false);
      }
    } catch (err) {
      console.error('Fetch progress error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const deliveryStats = progress?.delivery_stats || {};
  const progressData = progress?.progress || {};

  return (
    <div className="space-y-6" data-testid="campaign-progress-tracker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{progress?.campaign_name}</h2>
          <Badge className={
            progress?.status === 'running' ? 'bg-blue-500' :
            progress?.status === 'completed' ? 'bg-emerald-500' :
            'bg-gray-500'
          }>
            {progress?.status?.toUpperCase()}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Progress</span>
          <span className="font-bold">{progressData.percentage?.toFixed(1)}%</span>
        </div>
        <Progress value={progressData.percentage || 0} className="h-3" />
        <p className="text-xs text-muted-foreground text-center">
          {progressData.processed?.toLocaleString()} of {progressData.total?.toLocaleString()} messages processed
        </p>
      </div>

      {/* Delivery Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-blue-500">{deliveryStats.sent?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Sent</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-emerald-500">{deliveryStats.delivered?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-red-500">{deliveryStats.failed?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-amber-500">{deliveryStats.pending?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Rate */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-muted-foreground">Delivery Rate</p>
          <p className="text-4xl font-bold text-emerald-500">
            {deliveryStats.delivery_rate?.toFixed(1) || 0}%
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="outline" className="gap-2" onClick={() => window.location.href = `/campaigns/${campaignId}/delivery-report`}>
          <BarChart3 className="w-4 h-4" />
          View Full Report
        </Button>
        <Button variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Download Report
        </Button>
      </div>
    </div>
  );
}

// Delivery Report Viewer
export function DeliveryReportViewer({ campaignId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [campaignId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaignId}/delivery-report`,
        { withCredentials: true }
      );
      setReport(response.data);
    } catch (err) {
      console.error('Fetch report error:', err);
      toast.error('Failed to load delivery report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaignId}/delivery-report/download?format=${format}`,
        { 
          withCredentials: true,
          responseType: format === 'csv' ? 'blob' : 'json'
        }
      );

      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `delivery_report_${campaignId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const summary = report?.summary || {};
  const dispositions = report?.disposition_breakdown || {};

  return (
    <div className="space-y-6" data-testid="delivery-report-viewer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Delivery Report</h2>
          <p className="text-sm text-muted-foreground">{report?.campaign_name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleDownload('csv')}>
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-xl font-bold">{summary.total_audience?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-xl font-bold text-blue-500">{summary.sent?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Sent</p>
          </CardContent>
        </Card>
        <Card className="text-center bg-emerald-500/10">
          <CardContent className="p-3">
            <div className="text-xl font-bold text-emerald-500">{summary.delivered?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </CardContent>
        </Card>
        <Card className="text-center bg-red-500/10">
          <CardContent className="p-3">
            <div className="text-xl font-bold text-red-500">{summary.failed?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="p-3">
            <div className="text-xl font-bold text-emerald-500">{summary.delivery_rate || 0}%</div>
            <p className="text-xs text-muted-foreground">Delivery Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Disposition Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Disposition Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(dispositions).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(dispositions).map(([code, data]) => (
                <div key={code} className="flex items-center justify-between p-2 rounded bg-muted/30">
                  <div className="flex items-center gap-3">
                    {code.toLowerCase().includes('deliv') ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : code.toLowerCase().includes('fail') ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Timer className="w-4 h-4 text-amber-500" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{code}</p>
                      <p className="text-xs text-muted-foreground">{data.description}</p>
                    </div>
                  </div>
                  <Badge variant="outline">{data.count?.toLocaleString()}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No delivery reports received yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cost */}
      {report?.cost && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Campaign Cost</p>
              <p className="text-2xl font-bold">{report.cost.formatted || '₹0'}</p>
            </div>
            <IndianRupee className="w-8 h-8 text-muted-foreground" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default {
  CampaignScheduleModal,
  ExecutionConfirmationModal,
  CampaignProgressTracker,
  DeliveryReportViewer
};
